/**
 * BrandLearning
 *
 * Sección "Fuente de identidad" que aprende el estilo de la marca de fuentes
 * externas y AJUSTA los sliders automáticamente.
 *
 * Hoy mockeado:
 *  - Pegás URL del sitio + Analizar → 1.5s loader → "Detectamos: tono amigable,
 *    estrategia balanceada, palabras a evitar: garantizado / oferta única" →
 *    aplica los valores a los sliders.
 *  - Subís PDF de brand guidelines → spinner → mismos sliders + algunos chips
 *    de tono detectados ("Cercano", "Familiar", "No usar exclamaciones excesivas").
 *
 * En Fase 3 esto se conecta a un servicio real (scraping + LLM extraction).
 */

import { useRef, useState } from "react";
import { Palette, Globe, Upload, Loader2, Sparkles, FileText, Check } from "lucide-react";
import { Card } from "../../../shared/components/ui/Card";
import { Button } from "../../../shared/components/ui/Button";

interface LearningResult {
    source: "url" | "pdf";
    sourceLabel: string;
    detectedTone: number;
    detectedAggressiveness: number;
    detectedForbidden: string[];
    insights: string[];
}

interface BrandLearningProps {
    /** Callback que aplica los valores detectados a los sliders del page */
    onApply: (r: {
        tone: number;
        aggressiveness: number;
        addForbidden: string[];
    }) => void;
}

/**
 * 4 arquetipos de mock — la URL ingresada se hashea y se elige uno.
 * Así si el cliente prueba con distintas URLs ve respuestas distintas
 * (en producción esto sería scraping real + LLM).
 */
const URL_ARCHETYPES: LearningResult[] = [
    // 0. Casual / cercano (default para labistecca)
    {
        source: "url",
        sourceLabel: "placeholder",
        detectedTone: 72,
        detectedAggressiveness: 35,
        detectedForbidden: ["garantizado", "oferta única", "promoción exclusiva"],
        insights: [
            "Lenguaje cercano, familiar, en segunda persona",
            "Evitan exclamaciones excesivas y emojis fuertes",
            "Usan 'vos' (registro rioplatense)",
            "Foco en producto + tradición, no en descuentos agresivos",
        ],
    },
    // 1. Premium / sofisticado
    {
        source: "url",
        sourceLabel: "placeholder",
        detectedTone: 28,
        detectedAggressiveness: 22,
        detectedForbidden: ["barato", "promo", "remate", "liquidación"],
        insights: [
            "Tono formal, distinguido, en tercera persona",
            "Lenguaje aspiracional: 'experiencia', 'descubrir', 'reservar'",
            "Sin emojis ni signos de exclamación",
            "Foco en valor + ocasión, nunca en precio",
        ],
    },
    // 2. Agresivo / promo-heavy
    {
        source: "url",
        sourceLabel: "placeholder",
        detectedTone: 85,
        detectedAggressiveness: 88,
        detectedForbidden: [],
        insights: [
            "Urgencia constante: 'última oportunidad', '¡corre!', 'solo hoy'",
            "Mucho mayúsculas y emojis 🔥⏰💸",
            "Descuentos visibles en cada mensaje (30-50% off)",
            "Llamadas a la acción imperativas: '¡PEDÍ YA!'",
        ],
    },
    // 3. Italiano clásico / tradicional
    {
        source: "url",
        sourceLabel: "placeholder",
        detectedTone: 58,
        detectedAggressiveness: 28,
        detectedForbidden: ["fast food", "delivery exprés", "ultra-rápido"],
        insights: [
            "Tono cálido pero respetuoso, evoca tradición",
            "Referencias a recetas familiares, abuelas, Italia",
            "Pocos emojis, los justos (🍝, 🍷, 🇮🇹)",
            "Énfasis en producto artesanal vs masivo",
        ],
    },
];

/** Hashea un string a un índice estable */
function pickArchetypeIndex(url: string): number {
    const cleaned = url.toLowerCase().replace(/^https?:\/\//, "").replace(/\/$/, "");
    if (cleaned.includes("labistecca")) return 0; // default casual
    let h = 0;
    for (let i = 0; i < cleaned.length; i++)
        h = ((h << 5) - h + cleaned.charCodeAt(i)) | 0;
    return Math.abs(h) % URL_ARCHETYPES.length;
}

const MOCK_PDF_RESULT: LearningResult = {
    source: "pdf",
    sourceLabel: "Brand Guidelines 2026.pdf",
    detectedTone: 65,
    detectedAggressiveness: 28,
    detectedForbidden: ["100% garantizado", "imperdible", "último día"],
    insights: [
        "Brand voice: cálido, profesional, optimista",
        "Tono prohibido: agresivo, manipulador, FOMO",
        "Personalidad: anfitrión, no vendedor",
        "Paleta primaria: violeta + dorado (descartar rojos)",
    ],
};

export default function BrandLearning({ onApply }: BrandLearningProps) {
    const [url, setUrl] = useState("https://labistecca.com.ar");
    const [loading, setLoading] = useState<"url" | "pdf" | null>(null);
    const [result, setResult] = useState<LearningResult | null>(null);
    const [applied, setApplied] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleAnalyzeUrl = () => {
        if (!url.trim()) return;
        setLoading("url");
        setApplied(false);
        setTimeout(() => {
            const archetype = URL_ARCHETYPES[pickArchetypeIndex(url.trim())];
            const sourceLabel = url.trim().replace(/^https?:\/\//, "").replace(/\/$/, "");
            setResult({ ...archetype, sourceLabel });
            setLoading(null);
        }, 1500);
    };

    const handleAnalyzePdf = (file?: File) => {
        const f = file || fileInputRef.current?.files?.[0];
        if (!f) return;
        setLoading("pdf");
        setApplied(false);
        setTimeout(() => {
            setResult({ ...MOCK_PDF_RESULT, sourceLabel: f.name });
            setLoading(null);
        }, 1500);
    };

    const handleApply = () => {
        if (!result) return;
        onApply({
            tone: result.detectedTone,
            aggressiveness: result.detectedAggressiveness,
            addForbidden: result.detectedForbidden,
        });
        setApplied(true);
    };

    return (
        <Card className="p-5 relative overflow-hidden bg-gradient-to-br from-white to-violet-50/30">
            <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500/5 rounded-full blur-[60px] pointer-events-none" />

            <div className="flex items-center gap-3 mb-5 relative z-10">
                <div className="p-2 bg-violet-50 rounded-lg text-violet-500">
                    <Palette size={18} />
                </div>
                <div>
                    <h3 className="text-slate-900 font-bold text-base">
                        Fuente de identidad
                    </h3>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wide font-bold">
                        Aprendé el estilo automáticamente
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                {/* URL Analyzer */}
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                    <label className="text-xs text-slate-700 font-medium mb-2 flex items-center gap-2">
                        <Globe size={12} className="text-slate-400" />
                        URL del sitio web
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://tumarca.com"
                            disabled={loading !== null}
                            className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-slate-900 text-xs focus:outline-none focus:border-violet-300 transition-colors disabled:opacity-60"
                        />
                        <Button
                            className="!py-1.5 !px-3 text-[10px]"
                            onClick={handleAnalyzeUrl}
                            disabled={loading !== null || !url.trim()}
                        >
                            {loading === "url" ? (
                                <Loader2 size={12} className="animate-spin" />
                            ) : (
                                "Analizar"
                            )}
                        </Button>
                    </div>
                </div>

                {/* PDF Uploader */}
                <div
                    className={`bg-white p-3.5 rounded-xl border-2 border-slate-200 border-dashed flex flex-col items-center justify-center text-center transition-colors cursor-pointer group ${
                        loading === "pdf"
                            ? "bg-violet-50/50"
                            : "hover:bg-slate-50 hover:border-violet-300"
                    }`}
                    onClick={() =>
                        loading === null && fileInputRef.current?.click()
                    }
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => handleAnalyzePdf(e.target.files?.[0])}
                    />
                    <div className="flex items-center gap-2 text-slate-500 group-hover:text-violet-600 transition-colors">
                        {loading === "pdf" ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Upload size={14} />
                        )}
                        <span className="text-xs font-medium">
                            {loading === "pdf"
                                ? "Analizando PDF…"
                                : "Subir brand guidelines (PDF)"}
                        </span>
                    </div>
                </div>
            </div>

            {/* Resultado del análisis */}
            {result && loading === null && (
                <div className="mt-5 relative z-10 animate-fade-in">
                    <div className="bg-white border border-violet-200 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-2">
                                <div className="p-1.5 bg-violet-100 rounded-md text-violet-700">
                                    {result.source === "url" ? (
                                        <Globe size={12} />
                                    ) : (
                                        <FileText size={12} />
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-slate-900">
                                        Análisis completado
                                    </p>
                                    <p className="text-[10px] text-slate-500">
                                        Fuente: {result.sourceLabel}
                                    </p>
                                </div>
                            </div>
                            <Sparkles size={14} className="text-violet-500" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                            <DetectedStat
                                label="Tono detectado"
                                value={`${result.detectedTone}/100`}
                                hint={
                                    result.detectedTone > 70
                                        ? "Amigable"
                                        : result.detectedTone < 30
                                          ? "Formal"
                                          : "Neutral"
                                }
                            />
                            <DetectedStat
                                label="Estrategia"
                                value={`${result.detectedAggressiveness}/100`}
                                hint={
                                    result.detectedAggressiveness > 70
                                        ? "Agresiva"
                                        : result.detectedAggressiveness < 40
                                          ? "Conservadora"
                                          : "Balanceada"
                                }
                            />
                            <DetectedStat
                                label="Palabras detectadas a evitar"
                                value={`${result.detectedForbidden.length}`}
                                hint="ver abajo"
                            />
                        </div>

                        <div className="space-y-1.5 mb-4">
                            {result.insights.map((it, i) => (
                                <div
                                    key={i}
                                    className="flex items-start gap-2 text-xs text-slate-700"
                                >
                                    <span className="text-violet-500 mt-0.5">
                                        ·
                                    </span>
                                    {it}
                                </div>
                            ))}
                        </div>

                        <div className="flex flex-wrap gap-1.5 mb-4">
                            {result.detectedForbidden.map((w) => (
                                <span
                                    key={w}
                                    className="px-2 py-0.5 rounded bg-red-50 border border-red-100 text-red-600 text-[10px] font-medium"
                                >
                                    {w}
                                </span>
                            ))}
                        </div>

                        <Button
                            variant="primary"
                            onClick={handleApply}
                            disabled={applied}
                            className="w-full"
                        >
                            {applied ? (
                                <>
                                    <Check size={14} />
                                    Aplicado a las reglas
                                </>
                            ) : (
                                <>
                                    <Sparkles size={14} />
                                    Aplicar a mis reglas
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}
        </Card>
    );
}

function DetectedStat({
    label,
    value,
    hint,
}: {
    label: string;
    value: string;
    hint: string;
}) {
    return (
        <div className="bg-slate-50 rounded-lg p-2.5 border border-slate-100">
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">
                {label}
            </p>
            <p className="text-sm font-bold text-slate-900">{value}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{hint}</p>
        </div>
    );
}
